/**
 * Demo mode anonymization utilities.
 * All functions are deterministic — the same input always produces the same output
 * within a single page session (via module-level Maps).
 */

// ── Client name anonymization ───────────────────────────────────────────

import { DEMO_COMPANIES } from "@/lib/demo/companies";

const nameMap = new Map<string, string>();
let nameCounter = 0;

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

// The fabricated demo companies are the intended display names in demo mode —
// pass them through so the sales demo reads believably. Only real client
// names (e.g. the client switcher's full list) get the "Client A" treatment.
const DEMO_SAFE_NAMES = new Set([
  ...DEMO_COMPANIES.map((c) => c.name.toLowerCase()),
  "demo company",
]);

function nextLabel(): string {
  const idx = nameCounter++;
  if (idx < 26) return `Client ${LETTERS[idx]}`;
  return `Client ${LETTERS[Math.floor(idx / 26) - 1]}${LETTERS[idx % 26]}`;
}

export function anonymizeClientName(realName: string): string {
  const key = realName.trim().toLowerCase();
  if (DEMO_SAFE_NAMES.has(key)) return realName;
  if (!nameMap.has(key)) {
    nameMap.set(key, nextLabel());
  }
  return nameMap.get(key)!;
}

// ── URL anonymization ───────────────────────────────────────────────────

export function anonymizeUrl(url: string): string {
  if (!url) return url;
  try {
    const u = new URL(url);
    const segments = u.pathname.split("/").filter(Boolean);
    const maskedPath = segments.length > 0
      ? "/" + segments.map(() => "page").join("/")
      : "/";
    return `https://demo-client.com${maskedPath}`;
  } catch {
    // Not a valid absolute URL (e.g. just a path) — mask segments
    const segments = url.split("/").filter(Boolean);
    if (segments.length === 0) return "/";
    return "/" + segments.map(() => "page").join("/");
  }
}

// ── Favicon constants ───────────────────────────────────────────────────

export const DEMO_FAVICON_URL =
  "https://www.google.com/s2/favicons?domain=example.com&sz=32";
export const DEMO_FAVICON_URL_64 =
  "https://www.google.com/s2/favicons?domain=example.com&sz=64";
